/**
 * Workspace Context
 * Phase 7: Workspace Surface Layer
 * 
 * Provides React context for workspace management including:
 * - Current page tracking
 * - Page navigation and actions
 * - Sidebar state management
 * - Content synchronization
 */

import React, { createContext, useContext, useState, useCallback, ReactNode, useEffect } from 'react';
import { eventBus } from '../event-bus';
import { savePages, loadPages, saveCurrentPageId, loadCurrentPageId } from './pageStorage';
import { saveDatabase, createDefaultDatabase } from './databaseStorage';
import { getSurfaceRouter } from '../router/SurfaceRouter';

/**
 * Page reference type
 */
export interface PageRef {
  id: string;
  title: string;
  parentId?: string;
  type?: 'page' | 'database' | 'view';
}

/**
 * Workspace Context Value Type
 */
export interface WorkspaceContextValue {
  // State
  currentPageId: string | null;
  currentPage: PageRef | null;
  sidebarCollapsed: boolean;
  pages: PageRef[];
  initialized: boolean;

  // Page actions
  selectPage: (pageId: string) => Promise<void>;
  goToHome: () => void;
  createPage: (title: string, parentId?: string) => Promise<PageRef>;
  updatePage: (pageId: string, updates: Partial<PageRef>) => Promise<void>;
  deletePage: (pageId: string) => Promise<void>;
  renamePage: (pageId: string, newName: string) => Promise<void>;

  // Sidebar actions
  toggleSidebar: () => void;
  setSidebarCollapsed: (collapsed: boolean) => void;

  // Utility methods
  getPageById: (pageId: string) => PageRef | undefined;
  getChildPages: (parentId: string) => PageRef[];
  hasChildren: (pageId: string) => boolean;
}

/**
 * Workspace Context
 */
const WorkspaceContext = createContext<WorkspaceContextValue | undefined>(undefined);

/**
 * Workspace Provider Props
 */
export interface WorkspaceProviderProps {
  children: ReactNode;
}

/**
 * Default pages to create on first launch.
 * The Tasks entry is a database-typed page (id matches the seeded db-tasks).
 */
const DEFAULT_PAGES: PageRef[] = [
  {
    id: 'page-prd',
    title: 'Product Requirements Document',
    type: 'page'
  },
  {
    id: 'page-getting-started',
    title: 'Getting Started',
    type: 'page'
  },
  {
    id: 'page-daily-notes',
    title: 'Daily Notes',
    type: 'page'
  },
  {
    id: 'db-tasks',
    title: 'Tasks',
    type: 'database'
  }
];

/**
 * Seed the default databases that ship with the workspace.
 * Called once during init when storage is empty. Idempotent.
 */
function seedDefaultDatabases(): void {
  const tasksDb = createDefaultDatabase('db-tasks', 'Tasks');
  tasksDb.schema = {
    ...tasksDb.schema,
    properties: [
      { id: 'title', name: 'Name', type: 'title', visible: true, position: 0 },
      {
        id: 'status', name: 'Status', type: 'status', visible: true, position: 1,
        statusOptions: [
          { id: 'todo', name: 'To do', color: '#adb5bd', state: 'todo' },
          { id: 'in-progress', name: 'In progress', color: '#fbbf24', state: 'in_progress' },
          { id: 'done', name: 'Done', color: '#52b788', state: 'done' },
        ],
      },
      {
        id: 'priority', name: 'Priority', type: 'select', visible: true, position: 2,
        options: [
          { id: 'p-high', name: 'High', color: '#ef4444' },
          { id: 'p-med', name: 'Medium', color: '#fbbf24' },
          { id: 'p-low', name: 'Low', color: '#52b788' },
        ],
      },
      { id: 'due', name: 'Due', type: 'date', visible: true, position: 3, dateFormat: 'YYYY-MM-DD' },
    ],
  };
  tasksDb.items = [
    { id: 'item-1', databaseId: 'db-tasks', title: '🚀 Ship the FreshCards full mount', properties: [{ propertyId: 'status', value: 'in-progress' }, { propertyId: 'priority', value: 'p-high' }], createdAt: new Date(), updatedAt: new Date() },
    { id: 'item-2', databaseId: 'db-tasks', title: '📜 Author panel-card.md + refresh-card.md', properties: [{ propertyId: 'status', value: 'todo' }, { propertyId: 'priority', value: 'p-med' }], createdAt: new Date(), updatedAt: new Date() },
    { id: 'item-3', databaseId: 'db-tasks', title: '🌐 Deploy to space.minimax.io', properties: [{ propertyId: 'status', value: 'todo' }, { propertyId: 'priority', value: 'p-low' }], createdAt: new Date(), updatedAt: new Date() },
  ];
  saveDatabase(tasksDb);
}

/**
 * Workspace Provider Component
 */
export const WorkspaceProvider: React.FC<WorkspaceProviderProps> = ({ children }) => {
  const [currentPageId, setCurrentPageId] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState<PageRef | null>(null);
  const [sidebarCollapsed, setSidebarCollapsedState] = useState(false);
  const [pages, setPages] = useState<PageRef[]>([]);
  const [initialized, setInitialized] = useState(false);

  /**
   * Initialize workspace with default pages
   */
  const initializeWorkspace = useCallback(async () => {
    try {
      // Try to load from storage first
      const storedPages = loadPages();
      
      if (storedPages.length > 0) {
        // Use stored pages
        setPages(storedPages);
        setCurrentPageId(storedPages[0].id);
        setCurrentPage(storedPages[0]);
        setInitialized(true);
        eventBus.emit('workspace:initialized', { pages: storedPages });
      } else {
        // Create default pages if no stored pages
        setPages(DEFAULT_PAGES);
        savePages(DEFAULT_PAGES); // Save defaults to storage
        // Auto-select the first page (PRD)
        setCurrentPageId(DEFAULT_PAGES[0].id);
        setCurrentPage(DEFAULT_PAGES[0]);
        setInitialized(true);
        eventBus.emit('workspace:initialized', { pages: DEFAULT_PAGES });
      }
    } catch (error) {
      console.error('[WorkspaceContext] Error initializing workspace:', error);
    }
  }, []);

  /**
   * Load pages from storage
   */
  const loadPagesFromStorage = useCallback(() => {
    try {
      const stored = loadPages();
      if (stored.length > 0) {
        setPages(stored);
        if (!currentPageId) {
          setCurrentPageId(stored[0].id);
          setCurrentPage(stored[0]);
        }
      }
      eventBus.emit('workspace:pages-loaded', { pages });
    } catch (error) {
      console.error('[WorkspaceContext] Error loading pages:', error);
    }
  }, [currentPageId]);

  /**
   * Initialize provider - load from localStorage or create defaults
   * Restore previously selected page if available
   */
  useEffect(() => {
    console.log('[WorkspaceContext] Initializing...');
    
    // Use setTimeout to ensure this runs after mount
    const timer = setTimeout(() => {
      try {
        const stored = loadPages();
        const savedCurrentPageId = loadCurrentPageId();
        console.log('[WorkspaceContext] Loaded pages:', stored.length, 'Saved page ID:', savedCurrentPageId);
        
        if (stored.length > 0) {
          // Try to restore the previously selected page
          if (savedCurrentPageId) {
            const page = stored.find(p => p.id === savedCurrentPageId);
            if (page) {
              console.log('[WorkspaceContext] Restoring page:', page.title);
              setPages(stored);
              setCurrentPageId(page.id);
              setCurrentPage(page);
              setInitialized(true);
              return;
            } else {
              console.log('[WorkspaceContext] Saved page not found, showing home');
            }
          } else {
            console.log('[WorkspaceContext] No saved page, showing home');
          }
          setPages(stored);
          setCurrentPageId(null);
          setCurrentPage(null);
          setInitialized(true);
        } else {
          // Create default pages
          console.log('[WorkspaceContext] No stored pages, creating defaults');
          setPages(DEFAULT_PAGES);
          savePages(DEFAULT_PAGES);
          // Also seed the default databases (Tasks with 3 sample items)
          seedDefaultDatabases();
          setCurrentPageId(null);
          setCurrentPage(null);
          saveCurrentPageId(null);
          setInitialized(true);
        }
        console.log('[WorkspaceContext] Initialization complete');
      } catch (error) {
        console.error('[WorkspaceContext] Initialization error:', error);
        setPages(DEFAULT_PAGES);
        setCurrentPageId(null);
        setCurrentPage(null);
        setInitialized(true);
      }
    }, 0);
    
    return () => clearTimeout(timer);
  }, []);

  /**
   * Setup event listeners
   */
  useEffect(() => {
    const handlePageUpdated = (data: any) => {
      if (data.pages) {
        setPages(data.pages);
      }
    };

    const handlePageSelected = (data: any) => {
      if (data.pageId) {
        setCurrentPageId(data.pageId);
        const page = pages.find(p => p.id === data.pageId);
        if (page) {
          setCurrentPage(page);
        }
      }
    };

    eventBus.on('pages-loaded', handlePageUpdated);
    eventBus.on('page-selected', handlePageSelected);

    return () => {
      eventBus.off('pages-loaded', handlePageUpdated);
      eventBus.off('page-selected', handlePageSelected);
    };
  }, [pages]);

  /**
   * Select a page
   */
  const selectPage = useCallback(async (pageId: string) => {
    try {
      const page = pages.find(p => p.id === pageId);
      if (page) {
        console.log('[WorkspaceContext] selectPage:', page.title);
        setCurrentPageId(pageId);
        setCurrentPage(page);
        saveCurrentPageId(pageId); // Persist the selection
        eventBus.emit('page-selected', { pageId, page });
      }
    } catch (error) {
      console.error('[WorkspaceContext] Error selecting page:', error);
      throw error;
    }
  }, [pages]);

  /**
   * Go back to home/gallery view - deselect current page
   */
  const goToHome = useCallback(() => {
    console.log('[WorkspaceContext] goToHome called');
    setCurrentPageId(null);
    setCurrentPage(null);
    saveCurrentPageId(null); // Clear saved selection
    eventBus.emit('page-selected', { pageId: null, page: null });
    // Clear the URL too — otherwise a stale /page/:id in the address bar
    // would be re-hydrated on refresh and override the "home" state.
    try {
      getSurfaceRouter().updateBrowserUrl('/', true);
    } catch (error) {
      console.error('[WorkspaceContext] Failed to clear URL on goToHome:', error);
    }
  }, []);

  /**
   * Create a new page
   * @param title - The page title
   * @param parentId - Optional parent page id
   * @param type - Optional page type. 'database' creates a database-typed page with a default database.
   */
  const createPage = useCallback(async (title: string, parentId?: string, type: 'page' | 'database' = 'page'): Promise<PageRef> => {
    try {
      const newPage: PageRef = {
        id: `page-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        title,
        parentId,
        type
      };

      setPages(prev => {
        const updated = [...prev, newPage];
        savePages(updated); // Persist to storage
        return updated;
      });
      // If this is a database page, also create the database with default schema.
      // The page id doubles as the database id so lookups are direct.
      if (type === 'database') {
        const db = createDefaultDatabase(newPage.id, title);
        saveDatabase(db);
      }
      // Auto-select the newly created page
      setCurrentPageId(newPage.id);
      setCurrentPage(newPage);
      saveCurrentPageId(newPage.id); // Persist the selection
      eventBus.emit('page-created', { page: newPage });

      return newPage;
    } catch (error) {
      console.error('[WorkspaceContext] Error creating page:', error);
      throw error;
    }
  }, []);

  /**
   * Update a page
   */
  const updatePage = useCallback(async (pageId: string, updates: Partial<PageRef>) => {
    try {
      setPages(prev => {
        const updated = prev.map(p => p.id === pageId ? { ...p, ...updates } : p);
        savePages(updated); // Persist to storage
        return updated;
      });

      if (currentPageId === pageId) {
        setCurrentPage(prev => prev ? { ...prev, ...updates } : null);
      }

      eventBus.emit('page-updated', { pageId, updates });
    } catch (error) {
      console.error('[WorkspaceContext] Error updating page:', error);
      throw error;
    }
  }, [currentPageId]);

  /**
   * Delete a page
   */
  const deletePage = useCallback(async (pageId: string) => {
    try {
      setPages(prev => {
        const updated = prev.filter(p => p.id !== pageId);
        savePages(updated); // Persist to storage
        return updated;
      });

      if (currentPageId === pageId) {
        setCurrentPageId(null);
        setCurrentPage(null);
      }

      eventBus.emit('page-deleted', { pageId });
    } catch (error) {
      console.error('[WorkspaceContext] Error deleting page:', error);
      throw error;
    }
  }, [currentPageId]);

  /**
   * Rename a page
   */
  const renamePage = useCallback(async (pageId: string, newName: string) => {
    try {
      await updatePage(pageId, { title: newName });
      eventBus.emit('page-renamed', { pageId, newName });
    } catch (error) {
      console.error('[WorkspaceContext] Error renaming page:', error);
      throw error;
    }
  }, [updatePage]);

  /**
   * Toggle sidebar collapsed state
   */
  const toggleSidebar = useCallback(() => {
    setSidebarCollapsedState(prev => !prev);
  }, []);

  /**
   * Set sidebar collapsed state
   */
  const setSidebarCollapsed = useCallback((collapsed: boolean) => {
    setSidebarCollapsedState(collapsed);
  }, []);

  /**
   * Get page by ID
   */
  const getPageById = useCallback((pageId: string) => {
    return pages.find(p => p.id === pageId);
  }, [pages]);

  /**
   * Get child pages
   */
  const getChildPages = useCallback((parentId: string) => {
    return pages.filter(p => p.parentId === parentId);
  }, [pages]);

  /**
   * Check if page has children
   */
  const hasChildren = useCallback((pageId: string) => {
    return pages.some(p => p.parentId === pageId);
  }, [pages]);

  const value: WorkspaceContextValue = {
    // State
    currentPageId,
    currentPage,
    sidebarCollapsed,
    pages,
    initialized,

    // Page actions
    selectPage,
    goToHome,
    createPage,
    updatePage,
    deletePage,
    renamePage,

    // Sidebar actions
    toggleSidebar,
    setSidebarCollapsed,

    // Utility methods
    getPageById,
    getChildPages,
    hasChildren
  };

  return (
    <WorkspaceContext.Provider value={value}>
      {children}
    </WorkspaceContext.Provider>
  );
};

/**
 * useWorkspace Hook
 * 
 * Use this hook to access workspace management functionality
 * Must be used within a WorkspaceProvider
 */
export function useWorkspace(): WorkspaceContextValue {
  const context = useContext(WorkspaceContext);

  if (!context) {
    throw new Error('useWorkspace must be used within a WorkspaceProvider');
  }

  return context;
}

/**
 * useCurrentPage Hook
 * 
 * Returns only the current page and related actions
 */
export function useCurrentPage() {
  const context = useWorkspace();
  return {
    currentPageId: context.currentPageId,
    currentPage: context.currentPage,
    selectPage: context.selectPage,
    renamePage: context.renamePage
  };
}

/**
 * usePageActions Hook
 * 
 * Returns page manipulation methods
 */
export function usePageActions() {
  const context = useWorkspace();
  return {
    createPage: context.createPage,
    updatePage: context.updatePage,
    deletePage: context.deletePage,
    renamePage: context.renamePage
  };
}

/**
 * useSidebarState Hook
 * 
 * Returns sidebar state and actions
 */
export function useSidebarState() {
  const context = useWorkspace();
  return {
    collapsed: context.sidebarCollapsed,
    toggle: context.toggleSidebar,
    setCollapsed: context.setSidebarCollapsed
  };
}

export type { WorkspaceContextValue, PageRef };
